import React, { useState, useRef, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import apis from '@src/apis';
import { useHistory, useLocation } from 'react-router-dom';
import camelcaseKeys from 'camelcase-keys';
import queryString from 'query-string';
import actions from '@src/redux/actions';
import useFeatureFlags from '@src/hooks/useFeatureFlags';
import { FEATURE_KEYS } from '@src/configs/featureKeys';
import { StyledRecording } from './index.style';
import GridSentenceButton from './ActionButton/GridSentenceButton';
import RecordingHeader from './RecordingHeader';
import RecordingContent from './RecordingContent';
import RecordingActions from './RecordingActions';

const Recording = ({
  onPreviousStep,
  onNextStep,
  sentences,
  voiceRecording,
  audiosCurrent,
}) => {
  const history = useHistory();
  const location = useLocation();
  const { getFeatureValue } = useFeatureFlags();

  const dispatch = useDispatch();
  const [openGridSentenceDialog, setOpenGridSentenceDialog] = useState(false);

  // record
  const [isRecording, setIsRecording] = useState(false);
  const isFirstRender = useRef(true);
  const [audioDurationFail, setAudioDurationFail] = useState(false);

  // rehear
  const audioRef = useRef(null);
  const [isRehearing, setIsRehearing] = useState(false);
  const [audioToPlay, setAudioToPlay] = useState('');

  const { audios } = useSelector((state) => state.audioVoiceCloning);
  const { voice } = useSelector((state) => state.voiceVoiceCloning);
  const { user } = useSelector((state) => state.auth);
  const isVCNewRecording = getFeatureValue(FEATURE_KEYS.VC_NEW_RECORDING, {
    userId: user?.id,
    email: user?.email,
    phoneNumber: user?.phoneNumber,
  });

  const alreadyRecorded = voice?.submitAt;
  const [loadingUpdateAudio, setLoadingUpdateAudio] = useState(false);

  const changeUrlParams = (name, value) => {
    const url = new URL(window.location.href);
    url.searchParams.set(name, value);
    history.push(url.pathname + url.search);
  };

  const removeUrlParams = (name) => {
    const url = new URL(window.location.href);
    url.searchParams.delete(name);
    history.push(url.pathname + url.search);
  };

  const getDefaultIndex = () => {
    // Check if param have sentenceCode
    const { sentenceCode } = camelcaseKeys(
      queryString.parse(location.search || {}),
    );
    if (sentenceCode) {
      const index = sentences.findIndex(
        (sentence) => sentence.code === sentenceCode,
      );
      if (index !== -1) return index;
    }

    // Check if user has recorded before
    const lastRecordedIndex = sentences.findIndex(
      (sentence) => sentence.code === voiceRecording.lastSentenceCode,
    );
    if (sentences.length === 0) return 0;

    if (alreadyRecorded) {
      const audiosFailed = audios?.reduce((acc, audio, index) => {
        if (audio?.note) acc.push(index);
        return acc;
      }, []);

      if (audiosFailed.length > 0 && audiosFailed.includes(lastRecordedIndex))
        return lastRecordedIndex;

      if (audiosFailed.length > 0) return audiosFailed[0];
    }

    // If there is no recorded audio, return the last recorded index
    if (lastRecordedIndex !== -1) {
      changeUrlParams('sentenceCode', sentences[lastRecordedIndex].code);
      return lastRecordedIndex;
    }

    return 0;
  };
  const [currentIndex, setCurrentIndex] = useState(getDefaultIndex());

  const handleOpenGridSentenceDialog = () => {
    setOpenGridSentenceDialog(true);
  };

  const handleCloseGridSentenceDialog = () => {
    setOpenGridSentenceDialog(false);
  };

  //  TODO: refactor
  const handlePreviousSentence = () => {
    if (currentIndex === 0) {
      onPreviousStep();
      removeUrlParams('sentenceCode');
      return;
    }

    let previousIndex = currentIndex - 1;

    if (voiceRecording?.submitAt) {
      const audiosFailed = audios?.reduce((acc, audio, index) => {
        if (audio?.note) {
          acc.push(index);
        }
        return acc;
      }, []);

      if (audiosFailed.length > 0) {
        if (audiosFailed.length === 1) {
          onPreviousStep();
          removeUrlParams('sentenceCode');
          return;
        }
        const currentPositionIndex = audiosFailed.indexOf(currentIndex);
        if (currentPositionIndex === 0) {
          onPreviousStep();
          removeUrlParams('sentenceCode');
          return;
        }
        if (currentPositionIndex > 0)
          previousIndex = audiosFailed[currentPositionIndex - 1];
      }
    }

    setCurrentIndex(previousIndex);
    changeUrlParams('sentenceCode', sentences[previousIndex].code);
  };

  //  TODO: refactor

  const handleNextSentence = () => {
    const isLastSentence = currentIndex >= sentences.length - 1;
    if (isLastSentence) {
      onNextStep();
      removeUrlParams('sentenceCode');
      return;
    }

    let nextIndex = currentIndex + 1;

    // If there is a recorded voice and it has been submitted
    if (voiceRecording?.submitAt) {
      const audiosFailed = audios?.reduce((acc, audio, index) => {
        if (audio?.note) {
          acc.push(index);
        }
        return acc;
      }, []);

      // If there are failed audios, move to the next failed audio
      if (audiosFailed.length > 0) {
        if (audiosFailed.length === 1) {
          onNextStep();
          removeUrlParams('sentenceCode');
          return;
        }
        const currentPositionIndex = audiosFailed.indexOf(currentIndex);
        if (currentPositionIndex === audiosFailed.length - 1) {
          onNextStep();
          removeUrlParams('sentenceCode');
          return;
        }

        if (currentPositionIndex >= 0)
          nextIndex = audiosFailed[currentPositionIndex + 1];
      }
    }

    setCurrentIndex(nextIndex);
    changeUrlParams('sentenceCode', sentences[nextIndex].code);
  };

  const saveCurrentSentenceToVoice = async () => {
    if (currentIndex >= 0 && currentIndex < sentences.length) {
      const currentSentence = sentences[currentIndex];
      const voiceDataUpdate = {
        lastSentenceCode: currentSentence.code,
      };
      const response = await apis.voiceCloning.updateVoiceCloningVoice(
        voiceRecording.id,
        voiceDataUpdate,
      );
      if (!response?.status) {
        dispatch(
          actions.noti.push({
            severity: 'error',
            message: 'updateVoiceFailed',
          }),
        );
      } else {
        const updatedVoiceInfo = {
          ...voiceRecording,
          lastSentenceCode: currentSentence.code,
        };
        dispatch(actions.voiceVoiceCloning.addVoice(updatedVoiceInfo));
      }
    }
  };

  const handleAudioEnd = () => {
    audioRef.current.currentTime = 0;
    audioRef.current.pause();
    setIsRehearing(false);
  };

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      if (sentences[currentIndex]?.code)
        changeUrlParams('sentenceCode', sentences[currentIndex]?.code);
      return;
    }
    saveCurrentSentenceToVoice();
    setAudioDurationFail(false);
  }, [currentIndex]);

  useEffect(() => {
    if (audios.length > 0) setAudioToPlay(audios[currentIndex]?.audioLink);
  }, [audios, currentIndex]);

  useEffect(() => {
    if (audioToPlay) audioRef.current.src = audioToPlay;
    if (isRehearing) {
      if (audioRef.current) {
        audioRef.current.load();
        audioRef.current.play();
      }
    } else if (audioRef.current) audioRef.current.pause();
  }, [isRehearing]);

  useEffect(() => {
    if (sentences?.length > 0) {
      const index = getDefaultIndex();
      if (index !== currentIndex) setCurrentIndex(index);
    }
  }, [sentences]);

  return (
    <StyledRecording hasNote={audiosCurrent[currentIndex]?.note}>
      <RecordingHeader
        currentIndex={currentIndex}
        isRecording={isRecording}
        isRehearing={isRehearing}
        loadingUpdateAudio={loadingUpdateAudio}
        sentences={sentences}
        handlePreviousSentence={handlePreviousSentence}
        handleOpenGridSentenceDialog={handleOpenGridSentenceDialog}
      />
      <RecordingContent
        audios={audios}
        sentences={sentences}
        currentIndex={currentIndex}
        loadingUpdateAudio={loadingUpdateAudio}
        isRecording={isRecording}
        audioDurationFail={audioDurationFail}
      />
      <RecordingActions
        sentences={sentences}
        audios={audios}
        currentIndex={currentIndex}
        isRecording={isRecording}
        loadingUpdateAudio={loadingUpdateAudio}
        audioToPlay={audioToPlay}
        isRehearing={isRehearing}
        isVCNewRecording={isVCNewRecording}
        handleNextSentence={handleNextSentence}
        setIsRecording={setIsRecording}
        setAudioDurationFail={setAudioDurationFail}
        setLoadingUpdateAudio={setLoadingUpdateAudio}
        setIsRehearing={setIsRehearing}
      />
      {sentences.length > 0 && (
        <GridSentenceButton
          open={openGridSentenceDialog}
          onClose={handleCloseGridSentenceDialog}
          audiosArray={audios}
          currentSentenceIndex={currentIndex}
          onSelectSentence={(index) => {
            setCurrentIndex(index);
          }}
          onNextCheckInformation={onNextStep}
        />
      )}
      {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
      <audio ref={audioRef} onEnded={handleAudioEnd}>
        <source src={audioToPlay} type="audio/wav" />
      </audio>
    </StyledRecording>
  );
};

export default Recording;
